import type { PatternIconsDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorText, queryClient, type ApiError } from '../../api.ts';
import { paintWords } from '../../components/patterns/pictures.ts';
import { Button, ErrorLine, Section } from '../../components/ui.tsx';

/**
 * Settings → Development tools → Pattern pictures (spec "Pattern Mapping" §4.7): the small painted picture beside each
 * pattern a building can touch, on a job's Patterns page. How many are painted, how many are being painted, how many are
 * still drawn as their scale's tone, and painting the rest, an OpenAI image each, for an admin to start. Each copy of the
 * portal paints its own once, on the first deploy that has them while OpenAI is connected, and keeps each as painted,
 * never painting one again (Jason, 2026-10-17; domain/pattern-icons.ts).
 */

const patternIconsKey = ['patterns', 'pictures'];

export function PatternPicturesSection({ editable }: { editable: boolean }) {
  // While some are being painted it looks again now and then, as the step pictures' does.
  const { data } = useQuery<PatternIconsDto, ApiError>({
    queryKey: patternIconsKey,
    queryFn: () => api<PatternIconsDto>('/patterns/pictures'),
    staleTime: 60_000,
    refetchInterval: (query) => (query.state.data?.painting ? 15_000 : false),
  });
  // One more painted: a job's page shows it the next time it is opened, not after its pictures' long wait.
  const painted = data?.icons.length;
  useEffect(() => {
    if (painted) void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'project' && q.queryKey[2] === 'patterns' && q.queryKey[3] === 'pictures' });
  }, [painted]);
  return (
    <Section title="Pattern pictures">
      <p className="max-w-measure text-ink-2">
        The small painted picture beside each pattern on a job’s Patterns page, one for every pattern a building can touch. Each is painted once, on the first deploy while OpenAI is
        connected, and kept as painted, never painted again. Paint paints only those still without one, the ones whose painting failed among them; until a
        pattern has one, its scale’s tone stands where it would be.
      </p>
      {data ? <PatternPictures data={data} editable={editable} /> : null}
    </Section>
  );
}

/** How far the pictures have got, and Paint the rest. */
export function PatternPictures({ data: d, editable }: { data: PatternIconsDto; editable: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const total = d.total;
  const paint = async () => {
    setBusy(true);
    setErr(null);
    try {
      queryClient.setQueryData(patternIconsKey, await api<PatternIconsDto>('/patterns/pictures', { body: {} }));
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 text-sm">
      <p className="text-ink-2">
        {d.icons.length >= total ? (
          <>
            All {total} patterns have a painted picture.
          </>
        ) : (
          <>
            {d.icons.length} of {total} patterns {d.icons.length === 1 ? 'has' : 'have'} a painted picture
            {d.painting ? <span className="motion-safe:animate-pulse">; {d.painting} being painted now</span> : null}
            {d.missing ? `; ${d.missing} still drawn as their scale’s tone` : ''}
            {d.failed ? ` (${d.failed} of them could not be painted the last time)` : ''}.
          </>
        )}
      </p>
      {editable && d.missing && !d.painting ? (
        d.blocked ? (
          <p className="mt-1 text-ink-3">{d.blocked}</p>
        ) : (
          <div className="mt-1 flex flex-wrap items-baseline gap-x-4">
            <Button tone="quiet" busy={busy} onClick={() => void paint()}>
              {paintWords(d)}
            </Button>
            <span className="meta">Each is an image from OpenAI, paid for.</span>
          </div>
        )
      ) : null}
      <ErrorLine error={err} />
    </div>
  );
}
